import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsApi } from '../lib/api';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, Plus, X, Edit } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const ProductForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id) && location.pathname.includes('/edit');
  const isView = Boolean(id) && !location.pathname.includes('/edit');

  const [formData, setFormData] = useState({
    title_en: '',
    title_ar: '',
    description_en: '',
    description_ar: '',
    features_en: [] as (string | { value: string })[],
    features_ar: [] as (string | { value: string })[],
    use_cases_en: [] as (string | { value: string })[],
    use_cases_ar: [] as (string | { value: string })[],
  });

  const [newFeatureEn, setNewFeatureEn] = useState('');
  const [newFeatureAr, setNewFeatureAr] = useState('');
  const [newUseCaseEn, setNewUseCaseEn] = useState('');
  const [newUseCaseAr, setNewUseCaseAr] = useState('');

  const { data: product, isLoading } = useQuery({
    queryKey: ['products', id],
    queryFn: () => api.get(`/products/${id}?raw=true`).then((res: any) => res.data.data),
    enabled: isEdit || isView,
  });

  useEffect(() => {
    if (product && (isEdit || isView)) {
      setFormData({
        title_en: product.title_en || '',
        title_ar: product.title_ar || '',
        description_en: product.description_en || '',
        description_ar: product.description_ar || '',
        features_en: product.features_en ? product.features_en.map((f: any) => typeof f === 'string' ? f : f.value) : [],
        features_ar: product.features_ar ? product.features_ar.map((f: any) => typeof f === 'string' ? f : f.value) : [],
        use_cases_en: product.use_cases_en ? product.use_cases_en.map((u: any) => typeof u === 'string' ? u : u.value) : [],
        use_cases_ar: product.use_cases_ar ? product.use_cases_ar.map((u: any) => typeof u === 'string' ? u : u.value) : [],
      });
    }
  }, [product, isEdit, isView]);

  const createMutation = useMutation({
    mutationFn: (data: any) => productsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success(t('productCreated'));
      navigate('/products');
    },
    onError: () => {
      toast.error(t('errorOccurred'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => productsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success(t('productUpdated'));
      navigate('/products');
    },
    onError: () => {
      toast.error(t('errorOccurred'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const addFeature = (lang: 'en' | 'ar') => {
    const newFeature = lang === 'en' ? newFeatureEn : newFeatureAr;
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        [`features_${lang}`]: [...prev[`features_${lang}` as keyof typeof prev] as (string | { value: string })[], newFeature.trim()],
      }));
      if (lang === 'en') setNewFeatureEn('');
      else setNewFeatureAr('');
    }
  };

  const removeFeature = (lang: 'en' | 'ar', index: number) => {
    setFormData(prev => ({
      ...prev,
      [`features_${lang}`]: (prev[`features_${lang}` as keyof typeof prev] as (string | { value: string })[]).filter((_, i) => i !== index),
    }));
  };

  const addUseCase = (lang: 'en' | 'ar') => {
    const newUseCase = lang === 'en' ? newUseCaseEn : newUseCaseAr;
    if (newUseCase.trim()) {
      setFormData(prev => ({
        ...prev,
        [`use_cases_${lang}`]: [...prev[`use_cases_${lang}` as keyof typeof prev] as (string | { value: string })[], newUseCase.trim()],
      }));
      if (lang === 'en') setNewUseCaseEn('');
      else setNewUseCaseAr('');
    }
  };

  const removeUseCase = (lang: 'en' | 'ar', index: number) => {
    setFormData(prev => ({
      ...prev,
      [`use_cases_${lang}`]: (prev[`use_cases_${lang}` as keyof typeof prev] as (string | { value: string })[]).filter((_, i) => i !== index),
    }));
  };

  if (isLoading && (isEdit || isView)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4 rtl:space-x-reverse">
        <Button variant="outline" onClick={() => navigate('/products')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {i18n.language === 'ar' ? 'العودة' : 'Back'}
        </Button>
        <h1 className="text-3xl font-bold">
          {isView
            ? (i18n.language === 'ar' ? 'عرض المنتج' : 'View Product')
            : isEdit 
            ? (i18n.language === 'ar' ? 'تعديل المنتج' : 'Edit Product')
            : (i18n.language === 'ar' ? 'إضافة منتج جديد' : 'Add New Product')
          }
        </h1>
      </div>

      <form onSubmit={handleSubmit} className={`space-y-6 ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.language === 'ar' ? 'المعلومات الأساسية' : 'Basic Information'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">{i18n.language === 'ar' ? 'العنوان' : 'Title'}</Label>
                <Input
                  id="title"
                  name="title"
                  value={i18n.language === 'ar' ? formData.title_ar : formData.title_en}
                  onChange={(e) => {
                    if (i18n.language === 'ar') {
                      setFormData(prev => ({ ...prev, title_ar: e.target.value }));
                    } else {
                      setFormData(prev => ({ ...prev, title_en: e.target.value }));
                    }
                  }}
                  dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                  disabled={isView}
                />
              </div>
              {!isView && (
                <div className="text-sm text-gray-500">
                  {i18n.language === 'ar' ? 'تعديل العنوان باللغة الإنجليزية:' : 'Edit English title:'}
                  <Input
                    id="title_other"
                    name="title_other"
                    value={i18n.language === 'ar' ? formData.title_en : formData.title_ar}
                    onChange={(e) => {
                      if (i18n.language === 'ar') {
                        setFormData(prev => ({ ...prev, title_en: e.target.value }));
                      } else {
                        setFormData(prev => ({ ...prev, title_ar: e.target.value }));
                      }
                    }}
                    dir={i18n.language === 'ar' ? 'ltr' : 'rtl'}
                    className="mt-1"
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Description */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.language === 'ar' ? 'الوصف' : 'Description'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="description_en">{i18n.language === 'ar' ? 'الوصف (إنجليزي)' : 'Description (English)'}</Label>
                <Textarea
                  id="description_en"
                  name="description_en"
                  value={formData.description_en}
                  onChange={handleChange}
                  rows={4}
                  dir="ltr"
                  disabled={isView}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description_ar">{i18n.language === 'ar' ? 'الوصف (عربي)' : 'Description (Arabic)'}</Label>
                <Textarea
                  id="description_ar"
                  name="description_ar"
                  value={formData.description_ar}
                  onChange={handleChange}
                  rows={4}
                  dir="rtl"
                  disabled={isView}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.language === 'ar' ? 'المزايا' : 'Features'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* English Features */}
              <div className="space-y-4">
                <Label>{i18n.language === 'ar' ? 'المزايا (إنجليزي)' : 'Features (English)'}</Label>
                <div className="space-y-2">
                  {formData.features_en.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="flex-1 p-2 bg-gray-100 rounded text-sm" dir="ltr">
                        {typeof feature === 'string' ? feature : feature.value}
                      </span>
                      {!isView && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => removeFeature('en', index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {!isView && (
                    <div className="flex space-x-2 rtl:space-x-reverse">
                      <Input
                        value={newFeatureEn}
                        onChange={(e) => setNewFeatureEn(e.target.value)}
                        placeholder={i18n.language === 'ar' ? 'أضف ميزة جديدة...' : 'Add new feature...'}
                        dir="ltr"
                      />
                      <Button
                        type="button"
                        onClick={() => addFeature('en')}
                        disabled={!newFeatureEn.trim()}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Arabic Features */}
              <div className="space-y-4">
                <Label>{i18n.language === 'ar' ? 'المزايا (عربي)' : 'Features (Arabic)'}</Label>
                <div className="space-y-2">
                  {formData.features_ar.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="flex-1 p-2 bg-gray-100 rounded text-sm" dir="rtl">
                        {typeof feature === 'string' ? feature : feature.value}
                      </span>
                      {!isView && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => removeFeature('ar', index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {!isView && (
                    <div className="flex space-x-2 rtl:space-x-reverse">
                      <Input
                        value={newFeatureAr}
                        onChange={(e) => setNewFeatureAr(e.target.value)}
                        placeholder={i18n.language === 'ar' ? 'أضف ميزة جديدة...' : 'Add new feature...'}
                        dir="rtl"
                      />
                      <Button
                        type="button"
                        onClick={() => addFeature('ar')}
                        disabled={!newFeatureAr.trim()}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Use Cases */}
        <Card>
          <CardHeader>
            <CardTitle>{i18n.language === 'ar' ? 'حالات الاستخدام' : 'Use Cases'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* English Use Cases */}
              <div className="space-y-4">
                <Label>{i18n.language === 'ar' ? 'حالات الاستخدام (إنجليزي)' : 'Use Cases (English)'}</Label>
                <div className="space-y-2">
                  {formData.use_cases_en.map((useCase, index) => (
                    <div key={index} className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="flex-1 p-2 bg-gray-100 rounded text-sm" dir="ltr">
                        {typeof useCase === 'string' ? useCase : useCase.value}
                      </span>
                      {!isView && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => removeUseCase('en', index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {!isView && (
                    <div className="flex space-x-2 rtl:space-x-reverse">
                      <Input
                        value={newUseCaseEn}
                        onChange={(e) => setNewUseCaseEn(e.target.value)}
                        placeholder={i18n.language === 'ar' ? 'أضف حالة استخدام جديدة...' : 'Add new use case...'}
                        dir="ltr"
                      />
                      <Button
                        type="button"
                        onClick={() => addUseCase('en')}
                        disabled={!newUseCaseEn.trim()}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Arabic Use Cases */}
              <div className="space-y-4">
                <Label>{i18n.language === 'ar' ? 'حالات الاستخدام (عربي)' : 'Use Cases (Arabic)'}</Label>
                <div className="space-y-2">
                  {formData.use_cases_ar.map((useCase, index) => (
                    <div key={index} className="flex items-center space-x-2 rtl:space-x-reverse">
                      <span className="flex-1 p-2 bg-gray-100 rounded text-sm" dir="rtl">
                        {typeof useCase === 'string' ? useCase : useCase.value}
                      </span>
                      {!isView && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => removeUseCase('ar', index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {!isView && (
                    <div className="flex space-x-2 rtl:space-x-reverse">
                      <Input
                        value={newUseCaseAr}
                        onChange={(e) => setNewUseCaseAr(e.target.value)}
                        placeholder={i18n.language === 'ar' ? 'أضف حالة استخدام جديدة...' : 'Add new use case...'}
                        dir="rtl"
                      />
                      <Button
                        type="button"
                        onClick={() => addUseCase('ar')}
                        disabled={!newUseCaseAr.trim()}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button or Edit Button */}
        <div className="flex justify-end">
          {isView ? (
            <Button 
              type="button" 
              onClick={() => navigate(`/products/${id}/edit`)}
            >
              <Edit className="h-4 w-4 mr-2" />
              {i18n.language === 'ar' ? 'تعديل المنتج' : 'Edit Product'}
            </Button>
          ) : (
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              <Save className="h-4 w-4 mr-2" />
              {createMutation.isPending || updateMutation.isPending
                ? (i18n.language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
                : (isEdit
                  ? (i18n.language === 'ar' ? 'تحديث المنتج' : 'Update Product')
                  : (i18n.language === 'ar' ? 'إنشاء المنتج' : 'Create Product')
                )
              }
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ProductForm;
